import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlSegment } from '@angular/router';
import { Observable, from, empty } from 'rxjs';
import { ListingsService } from '../services/listings.service';
import { Listing } from '../classes/listing';
import { PreRenderService } from '../services/pre-render.service';
import { CommunicationService } from '../services/communication.service';
import { ListingCategory } from '../classes/enums';
import { catchError, map } from 'rxjs/operators';
import { UrlSanitizerService } from '../services/url-sanitizer.service';
import { UrlValidator } from './url-validator';

@Injectable()
export class ListingResolver extends UrlValidator implements Resolve<Listing> {

  constructor(
    private _listingsService: ListingsService,
    private _communicationService: CommunicationService,
    protected _urlSanitizerService: UrlSanitizerService,
    protected _preRenderService: PreRenderService,
    protected _router: Router
  ) {
    super(_urlSanitizerService, _router, _preRenderService);
   }

  private redirectErrorMessage = 'redirected';

  resolve(route: ActivatedRouteSnapshot, rstate: RouterStateSnapshot): Observable<Listing> {
    const url = rstate.root.firstChild.url;

    let listingIdWithBoardPrefix = '0';
    const listingIdRegex = /-(\w+_\w+)-lst/;
    // Example path "address-wp_idm00000001-123456-lst" to extract "wp_idm00000001-123456"
    const listingIdRegexSeventy3 = /-(\w+_\w+-\w+)-lst/;

    const pathToBeChecked = url.length === 4 ? url[3].path : url[2].path;
    let listingIdMatches = listingIdRegex.exec(pathToBeChecked);
    if (listingIdMatches && listingIdMatches.length === 2) listingIdWithBoardPrefix = listingIdMatches[1];
    else {
      listingIdMatches = listingIdRegexSeventy3.exec(pathToBeChecked);
      if (listingIdMatches && listingIdMatches.length === 2) listingIdWithBoardPrefix = listingIdMatches[1];
    }

    if (!this.isValidUrl(url)) return empty();

    return from(this._listingsService.getListingDetails(listingIdWithBoardPrefix)).pipe(
      map(listing => {
        if (this.shouldRedirect(listing)) {
          this.navigationRedirect(listing.redirectUrl);
          throw new Error(this.redirectErrorMessage);
        }
        const categoryRedirect: string = this.listingCategoryRedirectUrl(listing, url);
        if (categoryRedirect) {
          this.navigationRedirect(categoryRedirect);
          throw new Error(this.redirectErrorMessage);
        }

        this._communicationService.setCurrentListingType(listing.isCommercial ? ListingCategory.COMMERCIAL : listing.isLuxury ? ListingCategory.LUXURY : ListingCategory.RESIDENTIAL);
        return listing;
      }),
      catchError((err: Error) => {
        if (err.message !== this.redirectErrorMessage) {
          const fallbackUrl = url.length === 4 ? url[0] + '/' + url[1] + '/' + url[2] : url[0] + '/' + url[1];
          this.navigationRedirect(fallbackUrl);
        }
        return empty();
      }));
  }

  shouldRedirect(listing: Listing): boolean {
    return listing.redirectUrl && listing.redirectUrl !== '' && listing.redirectUrl.replace(/\\|\//g, '') !== listing.detailUrl.replace(/\\|\//g,'') ;
  }

  listingCategoryRedirectUrl(listing: Listing, url: UrlSegment[]): string {
    // a 4 segment URL is one that starts with commercial or luxury
    if (listing.isResidential && url.length === 4) return url.slice(1).join('/');
    else if (listing.isLuxury && url.length === 3) return `/luxury/${url.join('/')}`;
    else if (listing.isCommercial && url.length === 3) return `/commercial/${url.join('/')}`;
    return undefined;
  }
}
